import {
  combinedLightTheme,
  combinedDarkTheme,
} from '../design-system/tokens';
import { organisationTheme } from '../globals';

type DesignTokenTheme = typeof combinedLightTheme;

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export interface OrganisationThemeDefinition {
  id: string;
  name?: string;
  base?: 'light' | 'dark';
  tokens?: DeepPartial<DesignTokenTheme>;
}

export interface OrganisationThemeConfig {
  activeThemeId?: string;
  themes?: OrganisationThemeDefinition[];
}

const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const deepMergeSafe = <T>(base: T, overlay: unknown): T => {
  if (!isPlainObject(overlay)) {
    return base;
  }

  const result: Record<string, unknown> = Object.create(null);
  if (isPlainObject(base)) {
    Object.keys(base).forEach((key) => {
      result[key] = (base as Record<string, unknown>)[key];
    });
  }

  Object.keys(overlay).forEach((key) => {
    if (DANGEROUS_KEYS.has(key)) {
      return;
    }
    const overlayValue = overlay[key];
    if (isPlainObject(overlayValue)) {
      const baseValue = result[key];
      result[key] = deepMergeSafe(isPlainObject(baseValue) ? baseValue : {}, overlayValue);
    } else {
      result[key] = overlayValue;
    }
  });

  return result as T;
};

export const toReactJssTheme = (theme: OrganisationThemeDefinition): DesignTokenTheme =>
  deepMergeSafe(theme.base === 'dark' ? combinedDarkTheme : combinedLightTheme, theme.tokens);

const memoize = <T>(factory: () => T): (() => T) => {
  let value: T;
  let resolved = false;
  return () => {
    if (!resolved) {
      value = factory();
      resolved = true;
    }
    return value;
  };
};

const organisationThemes = (organisationTheme as OrganisationThemeConfig).themes ?? [];

export const THEME_REGISTRY: Record<string, () => DesignTokenTheme> = {
  light: () => combinedLightTheme,
  dark: () => combinedDarkTheme,
  ...Object.fromEntries(
    organisationThemes.map((theme) => [theme.id, memoize(() => toReactJssTheme(theme))]),
  ),
};

export const availableThemes: Array<{ id: string; name: string }> = [
  { id: 'light', name: 'Light' },
  { id: 'dark', name: 'Dark' },
  ...organisationThemes.map((theme) => ({ id: theme.id, name: theme.name || theme.id })),
];
